import { useQuery, useQueryClient } from '@tanstack/react-query';
import { calendarApi } from '../api';
import { CalendarEvent } from '@demind-inc/core';
import { useCallback } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

interface UseCalendarEventsParams {
  userId?: string;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

interface UseCalendarEventsQueryParams {
  queryKey: [
    'lifestack.calendar.events',
    { userId?: string; startDate?: string; endDate?: string },
  ];
}

export const useCalendarEvents = ({
  userId = '',
  startDate,
  endDate,
  enabled = true,
}: UseCalendarEventsParams) => {
  const calendarEventsQuery = useQuery({
    queryKey: ['lifestack.calendar.events', { userId, startDate, endDate }],
    queryFn: ({ queryKey }: UseCalendarEventsQueryParams) => {
      const [, { userId = '', startDate, endDate }] = queryKey;

      return calendarApi
        .getCalendarEvents(userId, startDate, endDate)
        .then(({ data }) => data as CalendarEvent[]);
    },
    refetchOnWindowFocus: enabled && !!userId,
    enabled: enabled && !!userId,
    staleTime: 15000, // 15s
  });

  return {
    ...calendarEventsQuery,
    events: calendarEventsQuery.data ?? [],
    isLoading: enabled ? calendarEventsQuery.isLoading : false,
  };
};

export const useCalendarEventsCache = () => {
  const queryClient = useQueryClient();

  const findInCache = useCallback(
    ({ userId }: { userId: string }) => {
      const cachedEvents = queryClient.getQueriesData<CalendarEvent[]>({
        queryKey: ['lifestack.calendar.events', { userId }],
      });

      const sorted = cachedEvents
        .map(([queryKey, events]) => {
          const queryState = queryClient.getQueryState(queryKey);
          return {
            data: events,
            timestamp: queryState?.dataUpdatedAt || 0,
          };
        })
        .sort((a, b) => b.timestamp - a.timestamp);

      if (sorted.length === 0) {
        return [];
      }
      return (sorted[0].data ?? []).filter((event): event is CalendarEvent => !!event);
    },
    [queryClient]
  );

  const findInCacheByDate = useCallback(
    (date: string, userId: string) => {
      const queries = queryClient.getQueriesData<CalendarEvent[]>({
        queryKey: ['lifestack.calendar.events', { userId }],
      });
      const matchingQueries = queries
        .filter(([queryKey]) => {
          const [_, params] = queryKey as ['lifestack.calendar.events', UseCalendarEventsParams];
          return dayjs(date).isBetween(params.startDate, params.endDate, 'day', '[]');
        })
        .map(([queryKey, data]) => {
          const queryState = queryClient.getQueryState(queryKey);
          return {
            data,
            timestamp: queryState?.dataUpdatedAt || 0,
          };
        })
        .sort((a, b) => b.timestamp - a.timestamp);

      if (matchingQueries.length === 0) {
        return [];
      }
      return (
        matchingQueries[0].data?.filter((i) => dayjs(i.start.date).isSame(dayjs(date), 'day')) ?? []
      );
    },
    [queryClient]
  );

  return { findInCache, findInCacheByDate };
};
